/**
 * The registration step's behavior half: the registration form of the held
 * flow and the privacy policy agreement it needs (registration-privacy-consent):
 * Continue goes nowhere until the box is ticked, and the ticked agreement
 * travels with the registration. Draws nothing.
 */
import { useCallback, useMemo, useState } from 'react';
import { LEGAL_PAGE_PATHS } from '@telify/shared/browser';
import type { WebAppFlowContextResponse } from '@/api/types';
import type { FormResponseInput } from '@/types/forms';

import { usePieceEmit, usePieceOptions, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { useFormPage, type FormPage } from '../../hooks/useWebForms';
import { contract, type RegistrationStepActions, type RegistrationStepEvents, type RegistrationStepState } from './contract';

export interface RegistrationStepInput extends PieceInput<RegistrationStepEvents> {
  readonly flowToken: string;
  readonly flowContext: WebAppFlowContextResponse;
  readonly offering: { readonly name: string; readonly path: string } | null;
}

export type RegistrationStepRuntime = PieceRuntime<typeof contract, RegistrationStepState, RegistrationStepActions> & {
  /** The form itself and its submit, for the standard drawing. */
  readonly page: FormPage;
  readonly submit: (responses: FormResponseInput[]) => void;
};

export function useRegistrationStep(input: RegistrationStepInput): RegistrationStepRuntime {
  const options = usePieceOptions(contract, input.options);
  const emit = usePieceEmit(input.onEvent);
  const page = useFormPage(input.flowToken, input.flowContext, input.offering);
  const [consented, setConsented] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const needsConsent = page.form?.privacyConsentRequired === true;

  const submit = useCallback(
    (responses: FormResponseInput[]) => {
      if (needsConsent && !consented) {
        setConsentError(true);
        return;
      }
      void page.submit(responses, { privacyAccepted: needsConsent && consented }).then((ok) => {
        if (ok) emit('registered', {});
      });
    },
    [needsConsent, consented, page, emit],
  );

  const actions = useMemo<RegistrationStepActions>(
    () => ({
      setConsent: (agreed) => {
        setConsented(agreed);
        if (agreed) setConsentError(false);
      },
      back: page.back,
    }),
    [page.back],
  );

  return {
    state: {
      status: page.failed ? 'failed' : page.form ? 'ready' : 'loading',
      consented,
      consentError,
      policyHref: LEGAL_PAGE_PATHS.privacyPolicy,
    },
    actions,
    options,
    page,
    submit,
  };
}
